import cx from "classnames";
import { useEffect, useState } from "react";

import BackgroundSection from "./Background/BackgroundSection";
import BoxPropertiesSection from "./Box Properties/BoxPropertiesSection";
import BoxStyleSection from "./Box Style/BoxStyleSection";
import BranchStyleSection from "./Branch Style/BranchStyleSection";
import FieldPropertiesSection from "./Field Properties/FieldPropertiesSection";
import HighlightSection from "./Highlight/HighlightSection";
import LinkViewSection from "./Link View/LinkViewSection";
import { appstr } from "@coreapp/utils/appstr";
import {
  Badge,
  Button,
  Flex,
  isNullish,
  Tabs,
  Text,
  ToggleButton,
  ToggleButtonGroup
} from "@theorgchartdev/storybook";
import { GetDoc } from "@/Utils/StateLib";
import { getHoverTipContent } from "@/Utils/Misc";
import { isModifiedSection, nameCodeMapping } from "./ViewRuleEditor.utils";
import {
  enumHierarchyTypes,
  kBoxOverlayRule,
  kBoxStyleRule,
  kBranchStyleOverlayRule,
  kFieldOverlayRule,
  kHighlightingRule,
  kInheritedRule,
  kMasterPageRule,
  kPositionBoxOverlayRule,
  kPositionBoxStyleRule,
  kPositionFieldOverlayRule
} from "@coreapp/constants";

const enumTab = {
  Position: 0,
  Employee: 1,
  Chart: 2
};

const tabs = [
  {
    header: "Position box",
    value: enumTab.Position
  },
  {
    header: "Employee box",
    value: enumTab.Employee
  },
  {
    header: "Chart styles",
    value: enumTab.Chart
  }
];

const sections = [
  { tab: enumTab.Position, value: kPositionBoxStyleRule, icon: "books", tip: "Rule box style" },
  {
    tab: enumTab.Position,
    value: kPositionFieldOverlayRule,
    icon: "paintbrush",
    tip: "Rule field"
  },
  {
    tab: enumTab.Position,
    value: kPositionBoxOverlayRule,
    icon: "paint-roller",
    tip: "Rule box"
  },
  { tab: enumTab.Employee, value: kBoxStyleRule, icon: "books", tip: "Rule box style" },
  {
    tab: enumTab.Employee,
    value: kFieldOverlayRule,
    icon: "paintbrush",
    tip: "Rule field"
  },
  {
    tab: enumTab.Employee,
    value: kBoxOverlayRule,
    icon: "paint-roller",
    tip: "Rule box"
  },
  { tab: enumTab.Chart, value: kBranchStyleOverlayRule, icon: "sitemap", tip: "Rule branch" },
  {
    tab: enumTab.Chart,
    value: kMasterPageRule,
    icon: "background_",
    tip: "Rule background"
  },
  { tab: enumTab.Chart, value: kInheritedRule, icon: "link", tip: "Rule link" },
  {
    tab: enumTab.Chart,
    value: kHighlightingRule,
    icon: "highlighter-line",
    tip: "Rule highlight"
  }
  //{ value: "Grouping", icon: "object-group", tip:"Rule grouping" },//Disabled for beta
];

export const ViewRuleEditorContent = props => {
  const doc = GetDoc();
  const isPositions = doc?.IsPositionDocument() ?? false;

  const [section, setSection] = useState(null);

  useEffect(() => {
    if (!props.initSection) {
      return;
    }

    setSection(props.initSection);
  }, [props.initSection]);

  const handleClearSection = () => {
    const rule = props.rule.Clone();
    rule.DeleteRuleAction(section);
    props.setRule(rule);
  };

  const handleSectionChange = value => {
    const targetSection = value[0];
    setSection(targetSection);
  };

  const handleTabChange = tabIndex => {
    let targetTab = tabIndex;
    if (!isPositions) {
      // Adjust target index if Position Box tab is not present
      targetTab += 1;
    }

    const targetSection = sections.find(item => item.tab === targetTab);
    setSection(targetSection.value);
  };

  //See QChartTemplateEditor line 558
  const sectionContentMap = {
    [kBoxStyleRule]: () => (
      <BoxStyleSection
        actionType={kBoxStyleRule}
        hierarchyType={enumHierarchyTypes.employees}
        rule={props.rule}
        setRule={props.setRule}
        customWidth={props.customWidth - 150}
        minWidth={props.minWidth}
      />
    ),
    [kPositionBoxStyleRule]: () => (
      <BoxStyleSection
        actionType={kPositionBoxStyleRule}
        hierarchyType={enumHierarchyTypes.positions}
        rule={props.rule}
        setRule={props.setRule}
        customWidth={props.customWidth - 150}
        minWidth={props.minWidth}
      />
    ),
    [kBoxOverlayRule]: () => (
      <BoxPropertiesSection
        actionType={kBoxOverlayRule}
        rule={props.rule}
        setRule={props.setRule}
      />
    ),
    [kPositionBoxOverlayRule]: () => (
      <BoxPropertiesSection
        actionType={kPositionBoxOverlayRule}
        rule={props.rule}
        setRule={props.setRule}
      />
    ),
    [kFieldOverlayRule]: () => (
      <FieldPropertiesSection
        objClassName="Box"
        actionType={kFieldOverlayRule}
        rule={props.rule}
        setRule={props.setRule}
        onClose={props.onClose}
      />
    ),
    [kPositionFieldOverlayRule]: () => (
      <FieldPropertiesSection
        objClassName="Position"
        actionType={kPositionFieldOverlayRule}
        rule={props.rule}
        setRule={props.setRule}
        onClose={props.onClose}
      />
    ),
    [kBranchStyleOverlayRule]: () => (
      <BranchStyleSection rule={props.rule} setRule={props.setRule} />
    ),
    [kMasterPageRule]: () => <BackgroundSection rule={props.rule} setRule={props.setRule} />,
    [kInheritedRule]: () => (
      <LinkViewSection
        rule={props.rule}
        setRule={props.setRule}
        currentTemplateId={props.currentTemplateId}
      />
    ),
    [kHighlightingRule]: () => <HighlightSection rule={props.rule} setRule={props.setRule} />
  };

  const tabObjects = tabs.map(tab => ({
    header: appstr(tab.header),
    value: tab.value,
    content: (
      <TabContent
        rule={props.rule}
        tabValue={tab.value}
        section={section}
        onSectionClear={handleClearSection}
        onSectionChange={handleSectionChange}
      >
        {sectionContentMap[section]?.()}
      </TabContent>
    )
  }));

  if (!isPositions) {
    // Drop the "Position Box" tab if not relevant
    tabObjects.shift();
  }

  const sectionObj = sections.find(item => item.value === section);
  const tabIndex = tabObjects.findIndex(tab => tab.value === sectionObj?.tab);

  return (
    <Tabs
      customClass={cx("oc-flex oc-flex-column oc-fill-height oc-overflow-hidden")}
      customContentClass={cx("oc-flex-1 oc-overflow-hidden")}
      tabSize={"medium"}
      tabIndex={tabIndex}
      onTabChange={handleTabChange}
      tabs={tabObjects}
    />
  );
};

const TabContent = props => {
  const commonBadgeProps = {
    color: "success",
    size: "small",
    border: false
  };

  return (
    <Flex height={"100%"} gap={"12px"} overflow={"hidden"} padding={"8px 0"}>
      <Flex className={"view-rule-toolbar"} justifyContent="center">
        <ToggleButtonGroup
          value={props.section}
          onChange={props.onSectionChange}
          exclusive={true}
          enforceValueSet={true}
          spacing={24}
          orientation={"vertical"}
        >
          {sections.map(item => {
            if (item.tab !== props.tabValue) {
              return null;
            }

            const isModified = isModifiedSection(item.value, props.rule);

            return (
              <Badge key={item.value} showBadge={isModified} {...commonBadgeProps}>
                <ToggleButton
                  data-testid={`view-rule-editor-section-${item.value}`}
                  tooltipProps={getHoverTipContent(item.tip, "left")}
                  size={"medium-small"}
                  value={item.value}
                  icon={item.icon}
                />
              </Badge>
            );
          })}
        </ToggleButtonGroup>
      </Flex>
      <Flex
        overflow={"hidden"}
        flex={1}
        direction={"column"}
        width={"100%"}
        height={"100%"}
        gap={"8px"}
      >
        <Flex
          className="view-rule-action-section-header"
          direction="row"
          justifyContent="space-between"
          width="100%"
        >
          <Text size={"large"}>{appstr(nameCodeMapping[props.section])}</Text>
          <Button
            disabled={!isModifiedSection(props.section, props.rule)}
            onClick={props.onSectionClear}
            modifier="danger"
            size="small"
            iconAlignEnd={false}
            iconOnly={false}
            iconProps={{
              modifier: "white",
              name: "circle-xmark"
            }}
          >
            {appstr("clearsearch")}
          </Button>
        </Flex>
        <div
          className={cx("ocn-list-scrollbar", "view-rule-action-section-content")}
          style={{ overflowY: "auto", paddingRight: "12px" }}
        >
          {props.children}
        </div>
      </Flex>
    </Flex>
  );
};
